import { useEffect, useMemo, useState } from 'react';
import { isDomesticShippingAddress } from '../../../../lib/outbound-composition';
import {
  useCancelSend,
  useComposition,
  useCreateComposition,
  useEmailTemplates,
  useGenerateComposition,
  useOutbox,
  useQueueComposition,
  useUpdateComposition,
  type EmailTemplateKey,
  type OutboundComposition,
} from '../hooks/useHatMail';

type FollowReason = 'product_link' | 'recraft' | 'appointment' | 'other';

function messageFromError(error: unknown): string {
  const raw = (error as Error)?.message || 'Something went wrong';
  try {
    const parsed = JSON.parse(raw);
    if (parsed.validation_errors?.length) return parsed.validation_errors[0].message;
    return parsed.error || raw;
  } catch {
    return raw;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      {children}
    </label>
  );
}

const inputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-ghw-gold focus:ring-2 focus:ring-ghw-gold/20';

export default function StartEmailView({ testing }: { testing: boolean }) {
  const { data: templates, isLoading, error: templateError } = useEmailTemplates();
  const create = useCreateComposition();
  const update = useUpdateComposition();
  const generate = useGenerateComposition();
  const queue = useQueueComposition();
  const cancel = useCancelSend();
  const { data: outbox } = useOutbox();

  const [templateKey, setTemplateKey] = useState<EmailTemplateKey | null>(null);
  const [composition, setComposition] = useState<OutboundComposition | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [internationalConfirmed, setInternationalConfirmed] = useState(false);
  const [useAddressOverride, setUseAddressOverride] = useState(false);
  const [address, setAddress] = useState({ address1: '', address2: '', city: '', province: '', postalCode: '', country: '', countryCode: '' });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [followOrderNumber, setFollowOrderNumber] = useState('');
  const [followOrderDate, setFollowOrderDate] = useState('');
  const [customOrderNumber, setCustomOrderNumber] = useState('');
  const [reasons, setReasons] = useState<FollowReason[]>([]);
  const [productLink, setProductLink] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [otherNotes, setOtherNotes] = useState('');
  const [finalSubject, setFinalSubject] = useState('');
  const [finalBody, setFinalBody] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const selectedTemplate = templates?.find((template) => template.template_key === templateKey) ?? null;
  const queuedItem = outbox?.find((item) => item.composition_id === composition?.id);
  const { data: currentComposition } = useComposition(
    composition?.id ?? '',
    composition?.status === 'queued',
  );
  const busy = create.isPending || update.isPending || generate.isPending || queue.isPending;
  const mutationError = create.error || update.error || generate.error || queue.error || cancel.error;

  const shipping = useMemo(() => {
    if (composition?.template_key !== 'shipping_confirmation') return null;
    return composition.fields as any;
  }, [composition]);

  useEffect(() => {
    if (composition?.status === 'queued' && currentComposition?.status === 'sent') {
      setComposition(currentComposition);
      setNotice('Email sent successfully.');
    }
  }, [composition?.status, currentComposition]);

  const chooseTemplate = (key: EmailTemplateKey) => {
    setTemplateKey(key);
    setComposition(null);
    setNotice(null);
    setFinalSubject('');
    setFinalBody('');
    setSelectedItems([]);
    setAddressConfirmed(false);
    setInternationalConfirmed(false);
    setUseAddressOverride(false);
    setFirstName('');
    setLastName('');
    setRecipientEmail('');
    setFollowOrderNumber('');
    setFollowOrderDate('');
    setCustomOrderNumber('');
    setReasons([]);
    setProductLink('');
    setAppointmentDate('');
    setOtherNotes('');
  };

  const loadShippingOrder = () => {
    if (!selectedTemplate || !orderNumber.trim()) return;
    create.mutate({ template_id: selectedTemplate.id, fields: { orderNumber: orderNumber.trim() } }, {
      onSuccess: ({ composition: next }) => {
        setComposition(next);
        const fields = next.fields as any;
        setSelectedItems(fields.selectedItemIds ?? []);
        const nextAddress = fields.order?.shippingAddress;
        if (nextAddress) {
          setAddress(nextAddress);
          setUseAddressOverride(false);
        } else {
          setAddress({ address1: '', address2: '', city: '', province: '', postalCode: '', country: '', countryCode: '' });
          setUseAddressOverride(true);
        }
        setAddressConfirmed(false);
        setInternationalConfirmed(false);
        setNotice(fields.order?.verificationSource === 'controlled_test_evidence'
          ? 'Controlled test evidence loaded. Enter and confirm the shipping address before generating.'
          : 'Verified Shopify order loaded. Confirm the address and included items.');
      },
    });
  };

  const createFollowUp = () => {
    if (!selectedTemplate) return;
    create.mutate({
      template_id: selectedTemplate.id,
      fields: {
        customerFirstName: firstName,
        customerLastName: lastName,
        recipientEmail,
        orderNumber: followOrderNumber,
        orderDate: followOrderDate,
        reasons,
        productLink,
        appointmentDate,
        otherNotes,
      },
    }, {
      onSuccess: ({ composition: next }) => {
        setComposition(next);
        setNotice('Details saved. Generate the staff-reviewed draft when ready.');
      },
    });
  };

  const createCustomOptionsRequest = () => {
    if (!selectedTemplate) return;
    create.mutate({
      template_id: selectedTemplate.id,
      fields: {
        customerFirstName: firstName,
        customerLastName: lastName,
        recipientEmail,
        orderNumber: customOrderNumber,
      },
    }, {
      onSuccess: ({ composition: next }) => {
        setComposition(next);
        setNotice('Details saved. Generate the fixed custom-options checklist for staff review.');
      },
    });
  };

  const generateDraft = () => {
    if (!composition) return;
    if (composition.template_key === 'shipping_confirmation') {
      update.mutate({
        id: composition.id,
        fields: {
          selectedItemIds: selectedItems,
          addressConfirmed,
          internationalAddressConfirmed: internationalConfirmed,
          addressOverride: useAddressOverride ? address : null,
        },
      }, {
        onSuccess: ({ composition: updated }) => {
          setComposition(updated);
          generate.mutate(updated.id, {
            onSuccess: ({ composition: generated }) => {
              setComposition(generated);
              setFinalSubject(generated.final_subject ?? '');
              setFinalBody(generated.final_body ?? '');
              setNotice('Draft generated. Staff review is required before sending.');
            },
          });
        },
      });
    } else {
      generate.mutate(composition.id, {
        onSuccess: ({ composition: generated }) => {
          setComposition(generated);
          setFinalSubject(generated.final_subject ?? '');
          setFinalBody(generated.final_body ?? '');
          setNotice('Draft generated. Staff review is required before sending.');
        },
      });
    }
  };

  const queueReviewed = () => {
    if (!composition) return;
    update.mutate({ id: composition.id, final_subject: finalSubject, final_body: finalBody }, {
      onSuccess: ({ composition: reviewed }) => {
        setComposition(reviewed);
        queue.mutate(reviewed.id, {
          onSuccess: (result) => {
            setComposition({ ...reviewed, status: 'queued' });
            setNotice(result.duplicate
              ? 'This email is already scheduled to send.'
              : 'Sending in 65 seconds. Use Undo if you need to make a change.');
          },
        });
      },
    });
  };

  const toggleReason = (reason: FollowReason) => {
    setReasons((current) => current.includes(reason)
      ? current.filter((value) => value !== reason)
      : [...current, reason]);
  };

  const updateAddressField = (field: keyof typeof address, value: string) => {
    setAddress((current) => ({ ...current, [field]: value }));
    setAddressConfirmed(false);
    setInternationalConfirmed(false);
  };

  if (isLoading) return <p className="py-12 text-center text-sm text-gray-500">Loading starter emails...</p>;
  if (templateError) return <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Starter emails are not available for this account yet.</div>;

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ghw-gold">Guided composition</p>
            <h1 className="mt-1 text-xl font-semibold text-gray-900">Start a customer email</h1>
            <p className="mt-1 text-sm text-gray-500">Sender: retail@greeleyhatworks.com. One recipient. No CC, BCC, or attachments.</p>
          </div>
          {testing && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Admin testing</span>}
        </div>
      </section>

      {!composition && (
        <section className="grid gap-3 sm:grid-cols-2">
          {templates?.filter((template) =>
            template.template_key === 'shipping_confirmation'
              || template.template_key === 'follow_up_after_visit'
              || template.template_key === 'request_custom_options')
            .map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => chooseTemplate(template.template_key)}
                className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                  templateKey === template.template_key ? 'border-ghw-gold ring-2 ring-ghw-gold/15' : 'border-gray-200 hover:border-ghw-gold/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-semibold text-gray-900">{template.name}</h2>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">{template.status}</span>
                </div>
                <p className="mt-2 text-sm leading-5 text-gray-500">{template.description}</p>
              </button>
            ))}
        </section>
      )}

      {!composition && selectedTemplate?.template_key === 'shipping_confirmation' && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <Field label="Shopify order number">
            <input className={inputClass} value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} placeholder="#12406" />
          </Field>
          <button onClick={loadShippingOrder} disabled={busy || !orderNumber.trim()} className="mt-4 w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {create.isPending ? 'Verifying order...' : 'Load verified order'}
          </button>
        </section>
      )}

      {!composition && selectedTemplate?.template_key === 'follow_up_after_visit' && (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name"><input className={inputClass} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Last name"><input className={inputClass} value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
          </div>
          <Field label="Recipient email"><input type="email" className={inputClass} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Order number, optional"><input className={inputClass} value={followOrderNumber} onChange={(e) => setFollowOrderNumber(e.target.value)} /></Field>
            <Field label="Order date, optional"><input type="date" className={inputClass} value={followOrderDate} onChange={(e) => setFollowOrderDate(e.target.value)} /></Field>
          </div>
          <fieldset>
            <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Reason for follow-up</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {([
                ['product_link', 'Product link'], ['recraft', 'ReCraft page'],
                ['appointment', 'Appointment date'], ['other', 'Other'],
              ] as Array<[FollowReason, string]>).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-700">
                  <input type="checkbox" checked={reasons.includes(value)} onChange={() => toggleReason(value)} /> {label}
                </label>
              ))}
            </div>
          </fieldset>
          {reasons.includes('product_link') && <Field label="Product URL"><input type="url" className={inputClass} value={productLink} onChange={(e) => setProductLink(e.target.value)} /></Field>}
          {reasons.includes('recraft') && <p className="rounded-lg bg-ghw-cream/40 px-3 py-2 text-xs text-ghw-brown">The approved ReCraft page and form-and-photos guidance will be included.</p>}
          {reasons.includes('appointment') && <Field label="Appointment date"><input type="date" className={inputClass} value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)} /></Field>}
          {reasons.includes('other') && <Field label="Other notes"><textarea className={`${inputClass} min-h-24`} value={otherNotes} onChange={(e) => setOtherNotes(e.target.value)} /></Field>}
          <button onClick={createFollowUp} disabled={busy} className="w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {create.isPending ? 'Saving...' : 'Save follow-up details'}
          </button>
        </section>
      )}

      {!composition && selectedTemplate?.template_key === 'request_custom_options' && (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name"><input className={inputClass} value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Last name"><input className={inputClass} value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
          </div>
          <Field label="Recipient email"><input type="email" className={inputClass} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} /></Field>
          {selectedTemplate.version === 1 && <Field label="Order number, optional"><input className={inputClass} value={customOrderNumber} onChange={(e) => setCustomOrderNumber(e.target.value)} /></Field>}
          <p className="rounded-lg bg-ghw-cream/40 px-3 py-2 text-xs leading-5 text-ghw-brown">
            HatMail will include the approved Custom Hat Options page and the complete options checklist. Staff can personalize the message, but the link and checklist must remain intact.
          </p>
          <button onClick={createCustomOptionsRequest} disabled={busy} className="w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
            {create.isPending ? 'Saving...' : 'Save custom-options request'}
          </button>
        </section>
      )}

      {composition?.template_key === 'shipping_confirmation' && shipping && composition.status === 'draft' && (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className={`rounded-lg border px-3 py-2 text-sm ${shipping.order.verificationSource === 'controlled_test_evidence' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
            {shipping.order.verificationSource === 'controlled_test_evidence' ? 'Verified controlled test evidence' : 'Verified Shopify order'} <strong>{shipping.order.orderNumber}</strong> for {shipping.order.customerFirstName} {shipping.order.customerLastName}
            {shipping.order.verificationSource === 'controlled_test_evidence' && <span className="mt-1 block text-xs">The Shopify API is not the source for this test. Enter and confirm the shipping address manually.</span>}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Included items</p>
            <div className="space-y-2">
              {shipping.order.items.map((item: any) => (
                <label key={item.id} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                  <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => setSelectedItems((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id])} />
                  <span>{item.quantity} x {item.title}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700"><input type="checkbox" checked={useAddressOverride} disabled={shipping.order.verificationSource === 'controlled_test_evidence'} onChange={(e) => { setUseAddressOverride(e.target.checked); setAddressConfirmed(false); setInternationalConfirmed(false); }} /> {shipping.order.verificationSource === 'controlled_test_evidence' ? 'Enter the address for this controlled test' : 'Edit the address for this email only'}</label>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Address"><input className={inputClass} disabled={!useAddressOverride} value={address.address1} onChange={(e) => updateAddressField('address1', e.target.value)} /></Field>
            <Field label="Address 2"><input className={inputClass} disabled={!useAddressOverride} value={address.address2} onChange={(e) => updateAddressField('address2', e.target.value)} /></Field>
            <Field label="City"><input className={inputClass} disabled={!useAddressOverride} value={address.city} onChange={(e) => updateAddressField('city', e.target.value)} /></Field>
            <Field label="State / province"><input className={inputClass} disabled={!useAddressOverride} value={address.province} onChange={(e) => updateAddressField('province', e.target.value)} /></Field>
            <Field label="Postal code"><input className={inputClass} disabled={!useAddressOverride} value={address.postalCode} onChange={(e) => updateAddressField('postalCode', e.target.value)} /></Field>
            <Field label="Country"><input className={inputClass} disabled={!useAddressOverride} value={address.country} onChange={(e) => updateAddressField('country', e.target.value)} /></Field>
          </div>
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-700"><input className="mt-0.5" type="checkbox" checked={addressConfirmed} onChange={(e) => setAddressConfirmed(e.target.checked)} /> I confirmed the shipping address shown above.</label>
          {!isDomesticShippingAddress(address) && <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><input className="mt-0.5" type="checkbox" checked={internationalConfirmed} onChange={(e) => setInternationalConfirmed(e.target.checked)} /> I separately confirmed this international address.</label>}
          <button onClick={generateDraft} disabled={busy} className="w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Generating...' : 'Generate shipping email'}</button>
        </section>
      )}

      {composition?.template_key === 'follow_up_after_visit' && composition.status === 'draft' && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-600">Details are saved for {composition.recipient_email}. Generate the first draft, then review every line before sending.</p>
          <button onClick={generateDraft} disabled={busy} className="mt-4 w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{generate.isPending ? 'Generating...' : 'Generate follow-up email'}</button>
        </section>
      )}

      {composition?.template_key === 'request_custom_options' && composition.status === 'draft' && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-600">Details are saved for {composition.recipient_email}. Generate the fixed checklist, then review the message before sending.</p>
          <button onClick={generateDraft} disabled={busy} className="mt-4 w-full rounded-lg bg-ghw-brown px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{generate.isPending ? 'Generating...' : 'Generate custom-options email'}</button>
        </section>
      )}

      {composition && (composition.status === 'generated' || composition.status === 'queued' || composition.status === 'sent') && (
        <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Staff review</p><p className="mt-1 text-sm text-gray-600">To: {composition.recipient_email}</p></div>
            <span className="rounded-full bg-ghw-cream px-2.5 py-1 text-xs font-semibold text-ghw-brown">retail@</span>
          </div>
          <Field label="Subject"><input className={inputClass} disabled={composition.status !== 'generated'} value={finalSubject} onChange={(e) => setFinalSubject(e.target.value)} /></Field>
          <Field label="Email body"><textarea className={`${inputClass} min-h-72 font-sans leading-6`} disabled={composition.status !== 'generated'} value={finalBody} onChange={(e) => setFinalBody(e.target.value)} /></Field>
          {composition.status === 'generated' ? (
            <button onClick={queueReviewed} disabled={busy} className="w-full rounded-lg bg-emerald-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{queue.isPending ? 'Sending...' : 'Send'}</button>
          ) : composition.status === 'sent' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">Sent successfully through retail@greeleyhatworks.com.</div>
          ) : queuedItem ? (
            <div className={`flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-white ${queuedItem.status === 'failed' ? 'bg-red-700' : 'bg-gray-900'}`}>
              <span className="text-sm">
                {queuedItem.status === 'failed' ? 'Send failed. Restore the reviewed draft to try again.' : `Sending in ${queuedItem.seconds_remaining}s`}
                {queuedItem.status === 'failed' && queuedItem.last_error && <span className="mt-1 block text-xs text-red-100">{queuedItem.last_error}</span>}
              </span>
              <button onClick={() => cancel.mutate(queuedItem.id, { onSuccess: () => { setComposition({ ...composition, status: 'generated' }); setNotice('Send cancelled. The reviewed draft is restored.'); } })} className="rounded-md bg-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/25">{queuedItem.status === 'failed' ? 'Restore' : 'Undo'}</button>
            </div>
          ) : <p className="text-sm text-gray-500">Waiting for outbox status...</p>}
        </section>
      )}

      {notice && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">{notice}</div>}
      {mutationError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{messageFromError(mutationError)}</div>}
      {templateKey && <button type="button" onClick={() => chooseTemplate(templateKey)} className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700">Start over</button>}
    </div>
  );
}
